import React, { useEffect, useMemo, useState } from 'react';
import './index.css';
import SingleValidator from './components/SingleValidator';
import BatchValidator from './components/BatchValidator';
import {
  changePassword,
  createUser,
  downloadPdfReport,
  fetchMe,
  getProductToken,
  healthCheck,
  loginProduct,
  saveProfile,
  setUserPassword,
  setProductToken,
  updateUser,
} from './api/client';

const STORAGE_KEY = 'coretiq-product-state-v2';

const defaultState = {
  user: null,
  organization: {
    praxis_name: 'Praxis am Stadtpark',
    contact_email: 'salma@praxis.local',
    plan: 'free',
    created_at: '',
  },
  jobs: [],
  users: [],
  dashboard: {
    total_count: 0,
    ok_count: 0,
    warning_count: 0,
    error_count: 0,
    uncertain_count: 0,
    auto_approval_rate: 0,
    latest_jobs: [],
  },
  adminDashboard: {
    total_count: 0,
    warning_count: 0,
    error_count: 0,
    user_count: 0,
    active_user_count: 0,
    inactive_user_count: 0,
    active_staff_count: 0,
    inactive_staff_count: 0,
    warning_rate: 0,
    error_rate: 0,
    latest_jobs: [],
  },
};

const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: 'D' },
  { id: 'validate', label: 'Validierung', icon: 'V' },
  { id: 'history', label: 'Historie', icon: 'H' },
  { id: 'admin', label: 'Admin', icon: 'A', adminOnly: true },
  { id: 'profile', label: 'Praxisprofil', icon: 'P' },
  { id: 'privacy', label: 'Datenschutz', icon: 'S' },
];

function loadState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? { ...defaultState, ...JSON.parse(saved) } : defaultState;
  } catch {
    return defaultState;
  }
}

function initialView() {
  return window.location.pathname === '/privacy' ? 'privacy' : 'dashboard';
}

export default function App() {
  const [state, setState] = useState(loadState);
  const [view, setView] = useState(initialView);
  const [apiOk, setApiOk] = useState(null);
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  useEffect(() => {
    const onPop = () => setView(initialView());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    healthCheck()
      .then(() => setApiOk(true))
      .catch(() => setApiOk(false));
  }, []);

  useEffect(() => {
    if (!getProductToken()) return;
    refreshSession().catch(() => {
      setProductToken('');
      setState(prev => ({ ...prev, user: null }));
    });
  }, []);

  const metrics = useMemo(() => state.dashboard || buildDashboard(state.jobs), [state.dashboard, state.jobs]);
  const isAdmin = state.user?.role === 'admin';
  const visibleNavItems = useMemo(
    () => navItems.filter(item => !item.adminOnly || isAdmin),
    [isAdmin]
  );

  const navigate = (nextView) => {
    setView(nextView);
    const path = nextView === 'privacy' ? '/privacy' : '/';
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
    }
  };

  const refreshSession = async () => {
    const data = await fetchMe();
    setState(prev => ({
      ...prev,
      user: normalizeUser(data.user),
      ...normalizeProduct(data.product),
    }));
  };

  const login = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setLoginError('');
    try {
      const data = await loginProduct(form.get('email'), form.get('password'));
      setState(prev => ({
        ...prev,
        user: normalizeUser(data.user),
        ...normalizeProduct(data.product),
      }));
      navigate('dashboard');
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Login nicht moeglich. Backend erreichbar?');
    }
  };

  const logout = () => {
    setProductToken('');
    setState(prev => ({ ...prev, user: null }));
    navigate('dashboard');
  };

  const updateProfile = async (profile) => {
    const product = await saveProfile(profile);
    setState(prev => ({ ...prev, ...normalizeProduct(product) }));
  };

  const handleValidationResult = async (result) => {
    if (!result?.job) return;
    await refreshSession();
  };

  const mergeAdminPayload = (payload) => {
    setState(prev => ({
      ...prev,
      users: normalizeUsers(payload.users || prev.users),
      adminDashboard: normalizeAdminDashboard(payload.admin_dashboard || payload.adminDashboard || prev.adminDashboard, prev.jobs),
    }));
  };

  const handleCreateUser = async (payload) => {
    const data = await createUser(payload);
    mergeAdminPayload(data);
    return data.user;
  };

  const handleUpdateUser = async (userId, updates) => {
    const data = await updateUser(userId, updates);
    mergeAdminPayload(data);
    return data.user;
  };

  const handleSetUserPassword = async (userId, newPassword) => {
    const data = await setUserPassword(userId, newPassword);
    mergeAdminPayload(data);
    return data.user;
  };

  if (view === 'privacy' && !state.user) {
    return <PrivacyShell apiOk={apiOk} onLogin={() => navigate('dashboard')} />;
  }

  if (!state.user) {
    return <LoginScreen onLogin={login} apiOk={apiOk} error={loginError} onPrivacy={() => navigate('privacy')} />;
  }

  return (
    <div className="product-shell">
      <aside className="sidebar">
        <div className="brand-row">
          <div className="brand-mark">C</div>
          <div>
            <strong>CoreTiq</strong>
            <span>ICD-10 v0.5.0</span>
          </div>
        </div>
        <nav className="nav-list" aria-label="Produktnavigation">
          {visibleNavItems.map(item => (
            <button
              key={item.id}
              className={view === item.id ? 'nav-item active' : 'nav-item'}
              onClick={() => navigate(item.id)}
            >
              <span>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-status">
          <span className={apiOk ? 'status-dot ok' : apiOk === false ? 'status-dot error' : 'status-dot'} />
          {apiOk === null ? 'API wird geprueft' : apiOk ? 'API verbunden' : 'API offline'}
        </div>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div>
            <p className="eyebrow">{state.organization.plan === 'pro' ? 'Pro' : 'Free'} · Produkt-Modus</p>
            <h1>{titleFor(view)}</h1>
          </div>
          <div className="user-actions">
            <div className="user-chip">
              <span>{state.user.name.charAt(0).toUpperCase()}</span>
              <div>
                <strong>{state.user.name}</strong>
                <small>{state.user.role}</small>
              </div>
            </div>
            <button className="secondary-btn logout-btn" onClick={logout}>Logout</button>
          </div>
        </header>

        {view === 'dashboard' && <Dashboard metrics={metrics} jobs={state.jobs} organization={state.organization} />}
        {view === 'validate' && <ValidationWorkspace onResult={handleValidationResult} jobs={state.jobs} />}
        {view === 'history' && <History jobs={state.jobs} />}
        {view === 'admin' && isAdmin && (
          <AdminPage
            users={state.users}
            dashboard={state.adminDashboard}
            onCreateUser={handleCreateUser}
            onUpdateUser={handleUpdateUser}
            onSetPassword={handleSetUserPassword}
          />
        )}
        {view === 'profile' && <Profile organization={state.organization} user={state.user} onSave={updateProfile} />}
        {view === 'privacy' && <PrivacyPage />}
      </main>
    </div>
  );
}

function LoginScreen({ onLogin, apiOk, error, onPrivacy }) {
  return (
    <main className="login-screen">
      <section className="login-panel">
        <div className="brand-row">
          <div className="brand-mark">C</div>
          <div>
            <strong>CoreTiq</strong>
            <span>Produkt-Modus</span>
          </div>
        </div>
        <h1>ICD-10 Validierung fuer Praxisteams</h1>
        <p>Login ist erforderlich, bevor Einzel- oder Batch-Pruefungen ausgefuehrt werden.</p>
        <form onSubmit={onLogin} className="login-form">
          <label>E-Mail<input name="email" type="email" defaultValue="salma@praxis.local" /></label>
          <label>Passwort<input name="password" type="password" autoComplete="current-password" /></label>
          {error && <div className="form-error">{error}</div>}
          <button type="submit" className="primary-btn">Einloggen</button>
        </form>
        <button className="link-btn" onClick={onPrivacy}>Datenschutz-Hinweis anzeigen</button>
        <div className="sidebar-status">
          <span className={apiOk ? 'status-dot ok' : apiOk === false ? 'status-dot error' : 'status-dot'} />
          {apiOk === null ? 'API wird geprueft' : apiOk ? 'API verbunden' : 'API offline'}
        </div>
      </section>
    </main>
  );
}

function PrivacyShell({ apiOk, onLogin }) {
  return (
    <main className="login-screen privacy-shell">
      <section className="privacy-panel">
        <PrivacyPage />
        <div className="privacy-actions">
          <button className="primary-btn" onClick={onLogin}>Zur Anmeldung</button>
          <div className="sidebar-status">
            <span className={apiOk ? 'status-dot ok' : apiOk === false ? 'status-dot error' : 'status-dot'} />
            {apiOk === null ? 'API wird geprueft' : apiOk ? 'API verbunden' : 'API offline'}
          </div>
        </div>
      </section>
    </main>
  );
}

function Dashboard({ metrics, jobs, organization }) {
  return (
    <div className="content-stack">
      <section className="hero-band">
        <div>
          <p className="eyebrow">Praxisprofil</p>
          <h2>{organization.praxis_name}</h2>
          <p>Pruef-Historie speichert in v0.5.0 nur Metadaten. Medizinische Freitexte werden nicht dauerhaft abgelegt.</p>
        </div>
        <div className="hero-kpi">
          <strong>{metrics.auto_approval_rate}%</strong>
          <span>Auto-Freigabe</span>
        </div>
      </section>

      <section className="metric-grid">
        <Metric label="Gepruefte Faelle" value={metrics.total_count} />
        <Metric label="Warnungen" value={metrics.warning_count} tone="warn" />
        <Metric label="Fehler" value={metrics.error_count} tone="error" />
        <Metric label="Unklare Faelle" value={metrics.uncertain_count} />
      </section>

      <section className="table-card">
        <div className="section-head compact">
          <div><p className="eyebrow">Letzte Pruefungen</p><h2>Pruef-Historie</h2></div>
        </div>
        <JobTable jobs={jobs.slice(0, 5)} compact />
      </section>
    </div>
  );
}

function ValidationWorkspace({ onResult, jobs }) {
  const [tab, setTab] = useState('batch');
  return (
    <div className="content-stack">
      <section className="tabbar">
        <button className={tab === 'batch' ? 'active' : ''} onClick={() => setTab('batch')}>Batch / CSV</button>
        <button className={tab === 'single' ? 'active' : ''} onClick={() => setTab('single')}>Einzelpruefung</button>
        <span>{jobs.length} gespeicherte Pruefjobs</span>
      </section>
      <section className="tool-surface">
        {tab === 'single'
          ? <SingleValidator />
          : <BatchValidator onResult={onResult} />}
      </section>
    </div>
  );
}

function History({ jobs }) {
  return (
    <section className="table-card">
      <div className="section-head">
        <div><p className="eyebrow">Pruef-Historie</p><h2>Gespeicherte Jobs</h2></div>
      </div>
      <JobTable jobs={jobs} />
    </section>
  );
}

function JobTable({ jobs, compact }) {
  if (!jobs.length) {
    return <div className="empty-state">Noch keine Batch- oder CSV-Pruefung gespeichert.</div>;
  }
  return (
    <div className="responsive-table jobs-table">
      <div className="table-row table-head">
        <span>Datei</span><span>Zeitpunkt</span><span>Gesamt</span><span>Warnungen</span><span>Fehler</span><span>Auto</span><span>Report</span>
      </div>
      {jobs.map(job => (
        <div className="table-row" key={job.job_id || job.id}>
          <span>{job.filename}</span>
          <span>{formatDate(job.created_at)}</span>
          <span>{job.total_count}</span>
          <span>{job.warning_count}</span>
          <span>{job.error_count}</span>
          <span>{job.auto_approval_rate}%</span>
          <span>
            {!compact && <button className="report-btn" onClick={() => downloadPdfReport(job.job_id)}>PDF</button>}
            {compact && <button className="report-btn" onClick={() => downloadPdfReport(job.job_id)}>PDF</button>}
          </span>
        </div>
      ))}
    </div>
  );
}

function AdminPage({ users, dashboard, onCreateUser, onUpdateUser, onSetPassword }) {
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const create = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setMessage('');
    setError('');
    try {
      const user = await onCreateUser({
        name: form.get('name'),
        email: form.get('email'),
        password: form.get('password'),
        role: form.get('role'),
      });
      event.currentTarget.reset();
      setMessage(`Mitarbeiter ${user.email} wurde angelegt.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Nutzer konnte nicht angelegt werden.');
    }
  };

  const changeRole = async (user, role) => {
    setMessage('');
    setError('');
    try {
      await onUpdateUser(user.id, { role });
      setMessage(`Rolle fuer ${user.email} wurde aktualisiert.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Rolle konnte nicht geaendert werden.');
    }
  };

  const toggleActive = async (user) => {
    setMessage('');
    setError('');
    try {
      const updated = await onUpdateUser(user.id, { active: !user.active });
      setMessage(`${updated.email} ist jetzt ${updated.active ? 'aktiv' : 'deaktiviert'}.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Status konnte nicht geaendert werden.');
    }
  };

  const setPassword = async (event, user) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setMessage('');
    setError('');
    try {
      await onSetPassword(user.id, form.get('new_password'));
      event.currentTarget.reset();
      setMessage(`Passwort fuer ${user.email} wurde gesetzt.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Passwort konnte nicht gesetzt werden.');
    }
  };

  return (
    <div className="content-stack">
      <section className="metric-grid admin-metrics">
        <Metric label="Nutzer gesamt" value={dashboard.user_count || users.length} />
        <Metric label="Aktive Mitarbeiter" value={dashboard.active_staff_count || 0} />
        <Metric label="Deaktivierte Mitarbeiter" value={dashboard.inactive_staff_count || 0} />
        <Metric label="Fehlerquote" value={`${dashboard.error_rate || 0}%`} tone="error" />
      </section>

      <section className="profile-card">
        <div className="section-head">
          <div><p className="eyebrow">Admin</p><h2>Mitarbeiter anlegen</h2></div>
        </div>
        <form id="admin-create-user-form" onSubmit={create} className="admin-form">
          <label>Name<input name="name" minLength="2" required /></label>
          <label>E-Mail<input name="email" type="email" required /></label>
          <label>Temporaeres Passwort<input name="password" type="password" minLength="10" required /></label>
          <label>Rolle
            <select name="role" defaultValue="staff">
              <option value="staff">staff</option>
              <option value="admin">admin</option>
            </select>
          </label>
          <button className="primary-btn" type="submit">Mitarbeiter anlegen</button>
        </form>
        {(message || error) && <div className={error ? 'form-error admin-message' : 'form-success admin-message'}>{error || message}</div>}
      </section>

      <section className="table-card">
        <div className="section-head">
          <div><p className="eyebrow">Admin</p><h2>Nutzerverwaltung</h2></div>
        </div>
        <div className="responsive-table users-table">
          <div className="table-row table-head">
            <span>Nutzer</span><span>Rolle</span><span>Status</span><span>Passwort</span>
          </div>
          {users.map(user => (
            <div className="table-row" key={user.id} data-email={user.email}>
              <span>
                {user.name}
                <small>{user.email}</small>
              </span>
              <span>
                <select value={user.role} onChange={(event) => changeRole(user, event.target.value)}>
                  <option value="staff">staff</option>
                  <option value="admin">admin</option>
                </select>
              </span>
              <span>
                <button className={user.active ? 'secondary-btn status-action' : 'primary-btn status-action'} onClick={() => toggleActive(user)}>
                  {user.active ? 'Deaktivieren' : 'Aktivieren'}
                </button>
              </span>
              <span>
                <form className="inline-password-form" onSubmit={(event) => setPassword(event, user)}>
                  <input name="new_password" type="password" minLength="10" placeholder="Temp. Passwort" required />
                  <button className="secondary-btn" type="submit">Setzen</button>
                </form>
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Profile({ organization, user, onSave }) {
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const canEditProfile = user?.role === 'admin';

  const save = async (event) => {
    event.preventDefault();
    if (!canEditProfile) return;
    const form = new FormData(event.currentTarget);
    setProfileMessage('');
    setProfileError('');
    try {
      await onSave({
        praxis_name: form.get('praxis_name'),
        contact_email: form.get('contact_email'),
        plan: form.get('plan'),
      });
      setProfileMessage('Praxisprofil wurde gespeichert.');
    } catch (err) {
      setProfileError(err.response?.data?.detail || 'Praxisprofil konnte nicht gespeichert werden.');
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setPasswordMessage('');
    setPasswordError('');
    try {
      await changePassword(form.get('current_password'), form.get('new_password'));
      event.currentTarget.reset();
      setPasswordMessage('Passwort wurde geaendert.');
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Passwort konnte nicht geaendert werden.');
    }
  };

  return (
    <div className="content-stack">
      <section className="profile-card">
        <div className="section-head">
          <div><p className="eyebrow">Praxisprofil</p><h2>Stammdaten</h2></div>
          <span className="plan-chip">{organization.plan}</span>
        </div>
        <form onSubmit={save} className="profile-form">
          <label>Praxisname<input name="praxis_name" defaultValue={organization.praxis_name} readOnly={!canEditProfile} /></label>
          <label>Kontakt-E-Mail<input name="contact_email" type="email" defaultValue={organization.contact_email} readOnly={!canEditProfile} /></label>
          <label>Plan
            <select name="plan" defaultValue={organization.plan || 'free'} disabled={!canEditProfile}>
              <option value="free">free</option>
              <option value="pro">pro</option>
            </select>
          </label>
          <label>Erstellt am<input value={formatDate(organization.created_at)} readOnly /></label>
          {canEditProfile && <button className="primary-btn" type="submit">Profil speichern</button>}
        </form>
        {!canEditProfile && <div className="readonly-note">Staff kann das Praxisprofil ansehen, aber nicht bearbeiten.</div>}
        {(profileMessage || profileError) && <div className={profileError ? 'form-error profile-message' : 'form-success profile-message'}>{profileError || profileMessage}</div>}
      </section>

      <section className="profile-card">
        <div className="section-head">
          <div><p className="eyebrow">Account</p><h2>Passwort aendern</h2></div>
        </div>
        <form onSubmit={savePassword} className="profile-form">
          <label>Aktuelles Passwort<input name="current_password" type="password" minLength="6" required /></label>
          <label>Neues Passwort<input name="new_password" type="password" minLength="10" required /></label>
          <button className="primary-btn" type="submit">Passwort aendern</button>
        </form>
        {(passwordMessage || passwordError) && <div className={passwordError ? 'form-error profile-message' : 'form-success profile-message'}>{passwordError || passwordMessage}</div>}
      </section>
    </div>
  );
}

function PrivacyPage() {
  return (
    <section className="privacy-page">
      <p className="eyebrow">Datenschutz und medizinische Sicherheit</p>
      <h1>Datenschutz-Hinweis</h1>
      <div className="privacy-grid">
        <article>
          <h2>Plausibilitaetspruefung</h2>
          <p>Das Tool prueft, ob ein medizinischer Freitext und ein ICD-10-Code wahrscheinlich zusammenpassen. Es trifft keine Diagnoseentscheidung.</p>
        </article>
        <article>
          <h2>Manuelle Kontrolle</h2>
          <p>Warnungen, Fehler und unklare Ergebnisse muessen weiterhin fachlich und abrechnungsrechtlich geprueft werden.</p>
        </article>
        <article>
          <h2>Keine Freitext-Historie</h2>
          <p>In v0.5.0 werden fuer Batch- und CSV-Pruefungen nur Metadaten gespeichert: Dateiname, Zaehler, Quote, Nutzer und Zeitpunkt.</p>
        </article>
        <article>
          <h2>Rollenmodell</h2>
          <p>Admin-Nutzer verwalten Mitarbeiter und Praxisdaten. Staff-Nutzer validieren und sehen die zugehoerige Praxis-Historie.</p>
        </article>
        <article>
          <h2>Praxisbetrieb</h2>
          <p>Vor produktiver Nutzung mit Gesundheitsdaten bleiben AVV, TOMs, Loeschkonzept, Hosting-Pruefung und Security Review erforderlich.</p>
        </article>
      </div>
    </section>
  );
}

function Metric({ label, value, tone }) {
  return (
    <article className={`metric-card ${tone || ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function normalizeUser(user) {
  return {
    id: user.id,
    name: user.name || 'Nutzer',
    email: user.email,
    role: user.role,
    active: user.active === undefined ? true : Boolean(user.active),
  };
}

function normalizeProduct(product) {
  const organization = product?.organization || {};
  const jobs = (product?.jobs || product?.uploads || []).map(normalizeJob);
  const dashboard = product?.dashboard || buildDashboard(jobs);
  return {
    organization: {
      ...defaultState.organization,
      ...organization,
    },
    jobs,
    users: normalizeUsers(product?.users || []),
    dashboard,
    adminDashboard: normalizeAdminDashboard(product?.admin_dashboard || product?.adminDashboard, jobs),
  };
}

function normalizeUsers(users) {
  return (users || []).map(normalizeUser);
}

function normalizeAdminDashboard(dashboard, jobs = []) {
  return {
    ...defaultState.adminDashboard,
    ...buildDashboard(jobs),
    ...(dashboard || {}),
  };
}

function normalizeJob(job) {
  const total = Number(job.total_count ?? job.total ?? 0);
  const ok = Number(job.ok_count ?? job.ok ?? 0);
  return {
    job_id: job.job_id || job.id,
    user_id: job.user_id,
    filename: job.filename || job.name || 'pruefung.csv',
    total_count: total,
    ok_count: ok,
    warning_count: Number(job.warning_count ?? job.warning ?? 0),
    error_count: Number(job.error_count ?? job.error ?? 0),
    uncertain_count: Number(job.uncertain_count ?? job.uncertain ?? 0),
    auto_approval_rate: Number(job.auto_approval_rate ?? (total ? Math.round((ok / total) * 1000) / 10 : 0)),
    created_at: job.created_at || job.date,
  };
}

function buildDashboard(jobs) {
  const total = jobs.reduce((sum, item) => sum + Number(item.total_count || 0), 0);
  const ok = jobs.reduce((sum, item) => sum + Number(item.ok_count || 0), 0);
  const warning = jobs.reduce((sum, item) => sum + Number(item.warning_count || 0), 0);
  const error = jobs.reduce((sum, item) => sum + Number(item.error_count || 0), 0);
  const uncertain = jobs.reduce((sum, item) => sum + Number(item.uncertain_count || 0), 0);
  return {
    total_count: total,
    ok_count: ok,
    warning_count: warning,
    error_count: error,
    uncertain_count: uncertain,
    auto_approval_rate: total ? Math.round((ok / total) * 1000) / 10 : 0,
    latest_jobs: jobs.slice(0, 5),
  };
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

function titleFor(view) {
  const titles = {
    dashboard: 'Dashboard',
    validate: 'Validierung',
    history: 'Pruef-Historie',
    admin: 'Admin',
    profile: 'Praxisprofil',
    privacy: 'Datenschutz',
  };
  return titles[view] || 'Dashboard';
}
